import { Alert, Button, Modal } from "react-bootstrap";
import { OrderPayment } from "../../../api/models/orderModel";
import { useEffect, useState } from "react";
import OrderPayments from "../OrdersReceivable/OrderPayments";
import { FormatDecimal } from "../../../api/helpers/formatters";
import {
    GetOrdersToDeliverViewModel,
    PrimeOrderPayments,
} from "../../../api/services/ordersServices";
import { t } from "i18next";

interface Props {
    order: GetOrdersToDeliverViewModel;
    onClose: () => void;
    onDelivered: (orderId: number, payments: Array<OrderPayment>) => void;
}

const DeliverOrder = ({ order, onClose, onDelivered }: Props) => {
    const [showPayments, setShowPayments] = useState<boolean>(false);
    const [payments, setPayments] = useState<Array<OrderPayment>>([]);

    const [errorText, setErrorText] = useState("");

    useEffect(() => {
        inicializarPagos();
    }, []);

    const inicializarPagos = async () => {
        const saldoInicial = order.totalFullfilled;
        setShowPayments(saldoInicial > 0);

        if (saldoInicial === 0) return;

        try {
            const primed = await PrimeOrderPayments();
            setPayments(primed);
        } catch (error) {
            setErrorText(error as string);
        }
    };

    const handlePaymentChanged = (orderPayments: Array<OrderPayment>) => {
        // Valida que los pagos no excedan el total del pedido
        const saldo = calcularSaldo(orderPayments);
        if (saldo < 0) {
            setErrorText(t("deliverOrder_payment_cannot_exceed_total"));
            return;
        }

        setErrorText("");
        setPayments(orderPayments);
    };

    const calcularSaldo = (orderPayments: Array<OrderPayment>) => {
        const sumPayments = orderPayments.reduce(
            (accumulator, currentObject) => {
                return accumulator + currentObject.amount;
            },
            0
        );
        return order.totalFullfilled - sumPayments;
    };

    const handleEntregar = () => {
        setErrorText("");

        if (!validarReferenciasFormaPago()) return;

        // Si queda saldo pendiente, pregunta si se desea entregar y dejar el saldo pendiente
        const saldo = calcularSaldo(payments);
        if (saldo > 0) {
            if (
                !window.confirm(
                    `${t("deliverOrder_payment_pending")} ${FormatDecimal(
                        saldo,
                        true
                    )}. ${t("deliverOrder_deliver_with_pending_payment")}`
                )
            )
                return;
        }

        onDelivered(order.id, payments);
    };

    const validarReferenciasFormaPago = () => {
        console.log(payments);
        const pagosSinReferencia = payments.filter(
            (p) =>
                !p.paymentReference &&
                p.paymentMethod.referenceRequired &&
                p.amount > 0
        );
        if (pagosSinReferencia.length === 0) return true;

        setErrorText(
            `${t("deliverOrder_payment_method_requires_reference_prefix")} ${
                pagosSinReferencia[0].paymentMethod.name
            } ${t("deliverOrder_payment_method_requires_reference_suffix")}.`
        );

        return false;
    };

    return (
        <Modal show={true} onHide={onClose}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {t("deliverOrder_deliver_order")} {order.orderNumber}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <strong>{order.customerName}</strong>
                </div>
                <div>
                    {t("deliverOrder_original_total")}:{" "}
                    {FormatDecimal(order.total, true)}
                </div>
                <div>
                    {t("deliverOrder_fulfilled_total")}:{" "}
                    <strong>
                        {FormatDecimal(order.totalFullfilled, true)}
                    </strong>
                </div>

                {showPayments && (
                    <div className="mt-2">
                        <OrderPayments
                            orderTotal={order.totalFullfilled}
                            orderPayments={payments}
                            onChange={handlePaymentChanged}
                        />
                    </div>
                )}
                {!showPayments && (
                    <div className="mt-3">
                        <p>{t("deliverOrder_set_as_fulfilled")}</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {errorText && (
                    <Alert className="mt-4 mb-2 w-100" variant="danger">
                        {errorText}
                    </Alert>
                )}
                <Button
                    disabled={errorText !== ""}
                    variant="success"
                    onClick={handleEntregar}
                >
                    {t("deliverOrder_deliver_order")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeliverOrder;
